import React, { useState } from "react";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const PROJECT_TYPES = [
    "Residential Permit",
    "ADU / Granny Flat",
    "Multifamily",
    "Architectural Plans",
    "Engineering Coordination",
    "Utility Connection",
    "Other",
];

export default function LeadForm({ source = "contact", compact = false, onSuccess }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        project_type: "",
        city: "",
        description: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
            toast.error("Please fill in your name, phone, and email.");
            return;
        }
        setLoading(true);
        try {
            await submitLead({ ...form, source });
            toast.success("Request received! Our team will reach out within 1 business day.");
            setForm({ name: "", phone: "", email: "", project_type: "", city: "", description: "" });
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            className="w-full space-y-4"
            data-testid={`lead-form-${source}`}
        >
            <div className={`grid ${compact ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2"} gap-4`}>
                <Field label="Full Name *">
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        className="input"
                        placeholder="John Smith"
                        required
                        data-testid={`lead-${source}-name`}
                    />
                </Field>
                <Field label="Phone *">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        className="input"
                        placeholder="(555) 123-4567"
                        required
                        data-testid={`lead-${source}-phone`}
                    />
                </Field>
                <Field label="Email *">
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="input"
                        placeholder="you@example.com"
                        required
                        data-testid={`lead-${source}-email`}
                    />
                </Field>
                <Field label="Project Type">
                    <select
                        value={form.project_type}
                        onChange={update("project_type")}
                        className="input"
                        data-testid={`lead-${source}-project-type`}
                    >
                        <option value="">Select a project type</option>
                        {PROJECT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </Field>
                <Field label="City / State" className="md:col-span-2">
                    <input
                        type="text"
                        value={form.city}
                        onChange={update("city")}
                        className="input"
                        placeholder="Los Angeles, CA"
                        data-testid={`lead-${source}-city`}
                    />
                </Field>
                <Field label="Project Description" className="md:col-span-2">
                    <textarea
                        value={form.description}
                        onChange={update("description")}
                        className="input min-h-[120px] resize-y"
                        placeholder="Tell us about your project scope, lot size, timeline..."
                        data-testid={`lead-${source}-description`}
                    />
                </Field>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`lead-${source}-submit`}
            >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                {loading ? "Sending..." : "Submit Request"}
            </button>

            <style>{`
                .input {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    border: 1px solid #E2E8F0;
                    background: #FFFFFF;
                    color: #0A2540;
                    font-family: 'Work Sans', sans-serif;
                    font-size: 0.95rem;
                    transition: border-color 200ms ease, box-shadow 200ms ease;
                    outline: none;
                }
                .input:focus {
                    border-color: #C5A059;
                    box-shadow: 0 0 0 3px rgba(197, 160, 89, 0.15);
                }
                .input::placeholder { color: #94A3B8; }
            `}</style>
        </form>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="text-xs font-semibold tracking-wide uppercase text-[#475569]">{label}</span>
            {children}
        </label>
    );
}
